import React, { Component } from "react";
import PropTypes from "prop-types";
import { area as d3Area } from "d3-shape";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas } from "../GenericComponent";
import { hexToRGBA, isDefined, functor } from "../utils";

class IchimokuCloudSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    const { yAccessor, base, bullishFill, bearishFill, opacity } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    const newBase = functor(base);

    ctx.save();

    // Draw bullish and bearish segments
    let currentSegment = [];
    let currentType = null;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const ichimoku = yAccessor(d);

      if (
        !ichimoku ||
        typeof ichimoku.spanA !== "number" ||
        typeof ichimoku.spanB !== "number"
      ) {
        // End current segment if data is invalid
        if (currentSegment.length > 0) {
          this.drawSegment(
            ctx,
            currentSegment,
            currentType === "bullish" ? bullishFill : bearishFill,
            opacity,
            xScale,
            yScale,
            xAccessor,
            newBase,
            moreProps,
          );
          currentSegment = [];
          currentType = null;
        }
        continue;
      }

      const segmentType =
        ichimoku.spanA > ichimoku.spanB ? "bullish" : "bearish";

      if (currentType === null) {
        // Start new segment
        currentType = segmentType;
        currentSegment.push(d);
      } else if (currentType === segmentType) {
        // Continue current segment
        currentSegment.push(d);
      } else {
        // Type changed, draw current segment and start new one
        this.drawSegment(
          ctx,
          currentSegment,
          currentType === "bullish" ? bullishFill : bearishFill,
          opacity,
          xScale,
          yScale,
          xAccessor,
          newBase,
          moreProps,
        );
        // Include previous point in new segment to avoid gaps
        const prevPoint = currentSegment[currentSegment.length - 1];
        currentSegment = [prevPoint, d];
        currentType = segmentType;
      }
    }

    // Draw remaining segment
    if (currentSegment.length > 0) {
      this.drawSegment(
        ctx,
        currentSegment,
        currentType === "bullish" ? bullishFill : bearishFill,
        opacity,
        xScale,
        yScale,
        xAccessor,
        newBase,
        moreProps,
      );
    }

    ctx.restore();
  }

  drawSegment(
    ctx,
    segment,
    fill,
    opacity,
    xScale,
    yScale,
    xAccessor,
    newBase,
    moreProps,
  ) {
    if (segment.length === 0) return;

    const { yAccessor } = this.props;

    ctx.fillStyle = hexToRGBA(fill, opacity);
    ctx.beginPath();

    const areaSeries = d3Area()
      .x((d) => xScale(xAccessor(d)))
      .y0((d) => newBase(yScale, d, moreProps))
      .y1((d) => yScale(yAccessor(d).spanA))
      .context(ctx);

    areaSeries(segment);
    ctx.fill();
  }

  renderSVG(moreProps) {
    const { yAccessor, base, bullishFill, bearishFill, opacity, className } =
      this.props;
    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    const newBase = functor(base);
    const paths = [];

    // Build bullish and bearish segments
    let currentSegment = [];
    let currentType = null;
    let segmentIndex = 0;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const ichimoku = yAccessor(d);

      if (
        !ichimoku ||
        typeof ichimoku.spanA !== "number" ||
        typeof ichimoku.spanB !== "number"
      ) {
        // End current segment if data is invalid
        if (currentSegment.length > 0) {
          paths.push(
            this.createPath(
              currentSegment,
              currentType === "bullish" ? bullishFill : bearishFill,
              opacity,
              xScale,
              yScale,
              xAccessor,
              newBase,
              className,
              segmentIndex,
            ),
          );
          segmentIndex++;
          currentSegment = [];
          currentType = null;
        }
        continue;
      }

      const segmentType =
        ichimoku.spanA > ichimoku.spanB ? "bullish" : "bearish";

      if (currentType === null) {
        // Start new segment
        currentType = segmentType;
        currentSegment.push(d);
      } else if (currentType === segmentType) {
        // Continue current segment
        currentSegment.push(d);
      } else {
        // Type changed, create path for current segment and start new one
        paths.push(
          this.createPath(
            currentSegment,
            currentType === "bullish" ? bullishFill : bearishFill,
            opacity,
            xScale,
            yScale,
            xAccessor,
            newBase,
            className,
            segmentIndex,
          ),
        );
        segmentIndex++;
        // Include previous point in new segment to avoid gaps
        const prevPoint = currentSegment[currentSegment.length - 1];
        currentSegment = [prevPoint, d];
        currentType = segmentType;
      }
    }

    // Add remaining segment
    if (currentSegment.length > 0) {
      paths.push(
        this.createPath(
          currentSegment,
          currentType === "bullish" ? bullishFill : bearishFill,
          opacity,
          xScale,
          yScale,
          xAccessor,
          newBase,
          className,
          segmentIndex,
        ),
      );
    }

    return <g>{paths}</g>;
  }

  createPath(
    segment,
    fill,
    opacity,
    xScale,
    yScale,
    xAccessor,
    newBase,
    className,
    index,
  ) {
    if (segment.length === 0) return null;

    const { yAccessor } = this.props;

    const areaSeries = d3Area()
      .x((d) => xScale(xAccessor(d)))
      .y0((d) => newBase(yScale, d))
      .y1((d) => yScale(yAccessor(d).spanA));

    const d = areaSeries(segment);

    return (
      <path
        key={`ichimoku-cloud-${index}`}
        className={className}
        d={d}
        stroke="none"
        fill={hexToRGBA(fill, opacity)}
      />
    );
  }

  render() {
    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getAxisCanvas}
        drawOn={["pan", "drag"]}
      />
    );
  }
}

IchimokuCloudSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  base: PropTypes.func.isRequired,
  bullishFill: PropTypes.string.isRequired,
  bearishFill: PropTypes.string.isRequired,
  opacity: PropTypes.number,
};

IchimokuCloudSeries.defaultProps = {
  className: "ichimoku-cloud",
  opacity: 0.2,
};

export default IchimokuCloudSeries;
